import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Flame, Star, Copy, Check, Trophy, Wallet, Trash2, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function Profile() {
  const { user, logout } = useAuth();
  const [refs, setRefs] = useState({ referral_code: "", count: 0, referrals: [] });
  const [copied, setCopied] = useState(false);

  // delete account
  const [showDelete, setShowDelete] = useState(false);
  const [delPassword, setDelPassword] = useState("");
  const [delErr, setDelErr] = useState(null);
  const [delLoading, setDelLoading] = useState(false);
  const [delDone, setDelDone] = useState(false);

  useEffect(() => {
    api.get("/users/me/referrals").then(({ data }) => setRefs(data)).catch(() => {});
  }, []);

  const copy = () => {
    navigator.clipboard.writeText(refs.referral_code || user?.referral_code || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const deleteAccount = async () => {
    if (!delPassword) { setDelErr("Enter your password to confirm."); return; }
    setDelLoading(true);
    setDelErr(null);
    try {
      await api.post("/users/me/delete", { password: delPassword });
      setDelDone(true);
      setTimeout(async () => {
        await logout();
        window.location.href = "/";
      }, 2200);
    } catch (e) {
      setDelErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setDelLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div className="brut-card p-6 sm:p-8 bg-[var(--cs-ink)] text-white relative overflow-hidden">
        <div className="absolute inset-0 dotgrid opacity-20 pointer-events-none" />
        <div className="text-xs uppercase tracking-widest font-bold text-slate-300">Profile</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">{user?.name}</h1>
        <p className="text-slate-300 mt-1">{user?.email} · {user?.city}</p>

        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Mini icon={Flame} label="Streak" value={`${user?.streak_days ?? 0}d`} color="var(--cs-flame)" />
          <Mini icon={Wallet} label="Earned" value={`$${(user?.total_earned || 0).toFixed(0)}`} color="var(--cs-money)" />
          <Mini icon={Trophy} label="Tasks" value={user?.tasks_completed ?? 0} color="#fff" />
          <Mini icon={Star} label="Rating" value={(user?.rating_avg || 0).toFixed(1)} color="var(--cs-flame-2)" />
        </div>
      </div>

      <div className="brut-card p-6 bg-[var(--cs-yellow)]">
        <div className="text-xs uppercase tracking-widest font-bold">Your referral code</div>
        <div className="mt-2 flex items-center gap-3 flex-wrap">
          <div className="font-display tabular text-4xl sm:text-5xl font-black tracking-tighter" data-testid="referral-code">
            {refs.referral_code || user?.referral_code || "—"}
          </div>
          <button onClick={copy} className="brut-btn brut-btn-ink" data-testid="copy-referral-btn">
            {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? "Copied!" : "Copy"}
          </button>
        </div>
        <p className="mt-3 font-semibold">
          Share with a friend. When they complete their first task, <strong>you both earn $5</strong>.
        </p>
      </div>

      <div className="brut-card p-6">
        <div className="text-xs uppercase tracking-widest font-bold text-[var(--cs-ink-2)]">Friends you brought in</div>
        <div className="font-display tabular text-5xl font-black mt-1">{refs.count}</div>
        {refs.referrals?.length > 0 ? (
          <div className="mt-4 space-y-2">
            {refs.referrals.map((r) => (
              <div key={r.id} className="flex items-center justify-between border-b-2 py-2" style={{ borderColor: "var(--cs-ink)" }} data-testid={`ref-row-${r.id}`}>
                <div className="font-bold">{r.name}</div>
                <div className="text-sm text-[var(--cs-ink-2)]">{r.tasks_completed} tasks done</div>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-3 text-[var(--cs-ink-2)]">No referrals yet. Share your code above to unlock the chain.</p>
        )}
      </div>

      <div className="brut-card p-6" style={{ borderColor: "#b91c1c" }}>
        <div className="text-xs uppercase tracking-widest font-bold text-red-700 flex items-center gap-1">
          <AlertTriangle size={12} /> Danger zone
        </div>
        <p className="mt-2 text-sm text-[var(--cs-ink-2)]">
          Deleting your account is permanent. Your profile, notifications, and personal data are removed.
          Transaction records are retained where required by law.
        </p>
        <button
          onClick={() => setShowDelete(true)}
          data-testid="delete-account-button"
          className="brut-btn mt-4 text-sm"
          style={{ background: "#fee2e2", borderColor: "#b91c1c", color: "#b91c1c", boxShadow: "3px 3px 0 0 #b91c1c" }}
        >
          <Trash2 size={14} /> Delete my account
        </button>
      </div>

      {showDelete && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-5" data-testid="delete-account-dialog">
          <div className="brut-card p-6 max-w-md w-full bg-white pop-in">
            {!delDone ? (
              <>
                <div className="flex items-start gap-3">
                  <div className="brut-card p-2 bg-red-50" style={{ borderColor: "#b91c1c", boxShadow: "3px 3px 0 0 #b91c1c" }}>
                    <AlertTriangle className="text-red-600" size={22} />
                  </div>
                  <div>
                    <h3 className="font-display text-2xl font-black">Delete your account?</h3>
                    <p className="text-[var(--cs-ink-2)] text-sm mt-1">
                      This can't be undone. Make sure you've withdrawn your wallet balance and finished any active tasks first.
                    </p>
                  </div>
                </div>
                <input
                  type="password"
                  data-testid="delete-password-input"
                  className="brut-input mt-4"
                  placeholder="Enter your password to confirm"
                  value={delPassword}
                  onChange={(e) => setDelPassword(e.target.value)}
                />
                {delErr && (
                  <div className="mt-3 brut-card p-3 bg-red-50 text-red-700 text-sm font-semibold" style={{ boxShadow: "3px 3px 0 0 #b91c1c", borderColor: "#b91c1c" }}>
                    {delErr}
                  </div>
                )}
                <div className="mt-5 flex justify-end gap-2">
                  <button onClick={() => { setShowDelete(false); setDelErr(null); setDelPassword(""); }} className="brut-btn" data-testid="delete-cancel">
                    Keep my account
                  </button>
                  <button
                    onClick={deleteAccount}
                    disabled={delLoading}
                    className="brut-btn"
                    style={{ background: "#b91c1c", color: "#fff", borderColor: "#7f1d1d", boxShadow: "3px 3px 0 0 #7f1d1d" }}
                    data-testid="delete-confirm"
                  >
                    {delLoading ? "Deleting…" : "Delete forever"}
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center" data-testid="delete-success">
                <CheckCircle2 className="mx-auto text-[var(--cs-money-2)]" size={42} />
                <h3 className="font-display text-2xl font-black mt-3">Account deleted.</h3>
                <p className="text-[var(--cs-ink-2)] text-sm mt-2">Sorry to see you go. Taking you home…</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Mini({ icon: Icon, label, value, color }) {
  return (
    <div className="brut-card p-3 bg-white text-[var(--cs-ink)]">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--cs-ink-2)]">{label}</span>
        <Icon size={14} style={{ color }} />
      </div>
      <div className="font-display tabular text-2xl font-black mt-1 leading-none">{value}</div>
    </div>
  );
}
